import PropTypes from 'prop-types';
import Link from 'next/link';
import _find from 'lodash/find';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import MaterialLink from './material-link';
import MaterialLinkButton from './material-link-button';
import Markdown from './markdown';
import ImageUpload from './image-upload';
import SocialLinks from './social-links';
import { useAppContext } from '../contexts/app';
import GroupListMembership from './group-list-membership';

const useStyles = makeStyles((theme) => ({
  list: {
    listStyleType: 'none',
    margin: 0,
    padding: 0
  },
  listItem: {
    margin: theme.spacing(2, 0, 3, 0),
    padding: 0,
    display: 'flex',
  },
  groupImageWrapper: {
    width: 80,
    flexShrink: 0,
    textAlign: 'center',
    marginRight: theme.spacing(2)
  },
  content: {
    flexGrow: 1,
    overflow: 'hidden',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  title: {
    margin: theme.spacing(0, 1, 0, 0),
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    marginTop: theme.spacing(0.5)
  },
  lockIcon: {
    width: 12,
    height: 12,
    marginRight: theme.spacing(0.5),
    paddingBottom: 2,
    opacity: 0.8
  },
  summary: {
    marginTop: theme.spacing(1)
  },
  groupImage: {
    maxWidth: 80,
    maxHeight: 60
  },
}));

const GroupList = ({ groups, loading, emptyMessage }) => {
  const classes = useStyles();
  const { loggedInUser } = useAppContext();

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!groups || !groups.length) {
    return <p>{emptyMessage}</p>;
  }

  return (
    <ul className={classes.list}>
      {groups.map((group) => {
        const membership = loggedInUser ? _find(loggedInUser.groupMembers, (gm) => gm.group.id === group.id) : null;
        return (
          <li key={group.id} className={classes.listItem}>
            <div className={classes.groupImageWrapper}>
              {group.image && (
                <Link href="/groups/[slug]" as={`/groups/${group.slug}`} title={group.name}>
                  <ImageUpload record={group} size="80x60i" className={classes.groupImage} />
                </Link>
              )}
            </div>

            <div className={classes.content}>
              <div className={classes.titleWrapper}>
                <Typography variant='h3' className={classes.title}>
                  {group.found ? group.name : (
                    <MaterialLink href="/groups/[slug]" as={`/groups/${group.slug}`}>
                      {group.name}
                    </MaterialLink>
                  )}
                </Typography>
                {loggedInUser && <GroupListMembership group={group} membership={membership} />}
              </div>

              {!group.public && (
                <div className={classes.subtitle}>
                  <img src="/static/lock-solid.svg" className={classes.lockIcon} />
                  Private
                </div>
              )}

              {group.summary && (
                <div className={classes.summary}>
                  <Markdown>{group.summary}</Markdown>
                </div>
              )}

              <SocialLinks source={group} inline compact />

              {group.upcomingEventsCount > 0 && (
                <div>
                  <MaterialLinkButton
                    size="small"
                    variant="outlined"
                    color="primary"
                    href="/groups/[slug]"
                    as={`/groups/${group.slug}`}
                    className={classes.upcomingEventsCount}
                  >
                    {group.upcomingEventsCount} upcoming event{group.upcomingEventsCount !== 1 ? 's' : ''}
                  </MaterialLinkButton>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

GroupList.propTypes = {
  groups: PropTypes.array,
  emptyMessage: PropTypes.node,
  loading: PropTypes.bool,
};

GroupList.defaultProps = {
  emptyMessage: 'No groups',
  loading: false,
};

export default GroupList;
